import { useEffect } from "react";
import { Outlet, useLocation } from "react-router";
import { UserContextProvider, useUserContext } from "../hooks/userContext";
import { HighLevelContextProvider } from "../hooks/HighLevelContext";
import { DesignerApplicationContextProvider } from "../hooks/designer-application-context";
import { NotificationProvider } from "../contexts/NotificationContext";
import { ImpersonationProvider, UserRole } from "../contexts/ImpersonationContext";
import { PaymentSettingsProvider } from "../contexts/PaymentSettingsContext";
import { FeatureFlagsProvider } from "../contexts/FeatureFlagsContext";
import { PresenceProvider } from "../contexts/PresenceContext";
import { ImpersonationBanner } from "./impersonation-banner";

const GA4_MEASUREMENT_ID = "G-QG2KWJDGHL";

const trackGa4PageView = (path: string) => {
  const gtag = (window as Window & {
    gtag?: (...args: unknown[]) => void;
  }).gtag;

  if (typeof gtag !== "function") return;

  gtag("config", GA4_MEASUREMENT_ID, {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

const RootLayoutContent = () => {
  const { user } = useUserContext();
  const location = useLocation();

  // Determine user's actual role
  const actualRole: UserRole | null = user
    ? user.accountType === 'Admin'
      ? 'Administrator'
      : user.accountType === 'Designer'
      ? 'Designer'
      : user.accountType === 'Homeowner'
      ? 'Homeowner'
      : null
    : null;

  const actualUserName = user ? `${user.firstName} ${user.lastName}` : null;

  useEffect(() => {
    const path = `${location.pathname}${location.search}${location.hash}`;
    trackGa4PageView(path);
  }, [location.hash, location.pathname, location.search]);


  return (
    <ImpersonationProvider actualRole={actualRole} actualUserName={actualUserName}>
      <HighLevelContextProvider>
        <ImpersonationBanner />
        <Outlet />
      </HighLevelContextProvider>
    </ImpersonationProvider>
  );
};

export const RootLayout = () => {
  return (
    <NotificationProvider>
      <PaymentSettingsProvider>
        <FeatureFlagsProvider>
          <UserContextProvider>
            <PresenceProvider>
              <DesignerApplicationContextProvider>
                <RootLayoutContent />
              </DesignerApplicationContextProvider>
            </PresenceProvider>
          </UserContextProvider>
        </FeatureFlagsProvider>
      </PaymentSettingsProvider>
    </NotificationProvider>
  );
};
